import { useMemo, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArtistProfileForm } from '@/components/ArtistProfileForm';
import { type Person } from '@littlebooker/dal';
import { apiGetPublic } from '@/lib/api';

type BannerVariant = 'info' | 'success' | 'warning' | 'error';

type StatusBannerProps = {
  icon?: ReactNode;
  title: string;
  body: string;
  variant: BannerVariant;
};

const bannerStyles: Record<BannerVariant, string> = {
  info: 'bg-blue-50 text-blue-900 border-blue-100',
  success: 'bg-emerald-50 text-emerald-900 border-emerald-100',
  warning: 'bg-amber-50 text-amber-900 border-amber-100',
  error: 'bg-red-50 text-red-900 border-red-100',
};

function StatusBanner({ icon, title, body, variant }: StatusBannerProps) {
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${bannerStyles[variant]}`}>
      <div className="mt-0.5 text-lg">{icon}</div>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm/relaxed opacity-90">{body}</p>
      </div>
    </div>
  );
}

export default function ArtistInvitePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') ?? '';

  const {
    data: person,
    isLoading,
    isError,
    error,
  } = useQuery({
    // Fetch by token via the public API (server validates the token and bypasses
    // RLS); the slug is now only used for display/routing. Closes §5.3 hole (a).
    queryKey: ['public-artist', inviteToken],
    queryFn: () => apiGetPublic<Person>('/artist', { token: inviteToken }),
    enabled: Boolean(inviteToken),
  });

  const inviteIsValid = useMemo(() => {
    if (!person || !inviteToken) return false;
    return Boolean(person.invite_token && person.invite_token === inviteToken);
  }, [inviteToken, person]);

  const renderState = () => {
    if (!slug) {
      return (
        <StatusBanner
          variant="error"
          title="Missing artist identifier"
          body="We couldn't figure out which artist profile this invite belongs to. Double-check the link you were sent."
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      );
    }

    if (!inviteToken) {
      return (
        <StatusBanner
          variant="warning"
          title="Invite token missing"
          body="This link is missing the invite token. Ask your LittleBooker admin to resend it or copy the full link again."
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking your invite…
        </div>
      );
    }

    if (isError) {
      return (
        <StatusBanner
          variant="error"
          title="Something went wrong"
          body={error instanceof Error ? error.message : 'We could not load your invite yet. Refresh the page or try again later.'}
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      );
    }

    if (!person) {
      return (
        <StatusBanner
          variant="error"
          title="We can't find that artist"
          body="The invite link points to a profile that doesn't exist. Ask your admin to double-check the slug or send a new invite."
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      );
    }

    if (!person.invite_token) {
      return (
        <StatusBanner
          variant="warning"
          title="Invite not enabled yet"
          body="This artist doesn't have an invite token on file. Let your LittleBooker admin know so they can generate a new one."
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      );
    }

    if (!inviteIsValid) {
      return (
        <StatusBanner
          variant="error"
          title="Invite expired or invalid"
          body="The invite token in this link doesn't match what we have on file. Request a fresh link to continue."
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      );
    }

    return (
      <div className="space-y-6">
        <StatusBanner
          variant="success"
          title="Invite confirmed"
          body="You're in! Fill out the details below so the programming team has everything they need."
          icon={<CheckCircle2 className="h-5 w-5" />}
        />

        <ArtistProfileForm person={person} inviteToken={inviteToken} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/50">
      <div className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center gap-6 p-6">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">LittleBooker · Artist Portal</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight">Finish your profile</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Use the invite link your host sent you. Need help? Ask them to resend the link or reach out to your LittleBooker admin.
          </p>
        </div>
        <Card className="border-primary/10 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">{person?.is_collective ? 'Collective invite' : 'Artist invite'}</CardTitle>
            <CardDescription>Verify your link, then fill in the essentials so your host can publish your profile.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">{renderState()}</CardContent>
        </Card>
      </div>
    </div>
  );
}
